import React, { useState } from "react";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement
} from "@stripe/react-stripe-js";
import { useStripe } from "@stripe/react-stripe-js";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    Radio,
    RadioGroup,
    Typography,
    Grid,
    FormControlLabel,
    FormControl,
    DialogActions,
    useTheme,
    Alert
} from "@mui/material";
import { useAtom } from "jotai";
import {
    bookingDetailsAtom,
    getSelectedLocationAtom,
    getSelectedServiceAtom,
    orderSummaryAtom,
    getUpcomingBookingsDateTimeListAtom,
    timeDisplayInfoAtom,
    bookingTypeAtom,
    chargeAtom,
    setDetailAtom,
    isStripeChargeCardValidAtom
} from "../atoms/booking";
import {
    mbRecurringBookingRRuleAtom,
    recurringSettingsAtom
} from "../atoms/recurringSettings";
import {
    companyAtom,
    companyCollectsPaymentAtom,
    companyCurrencyAtom,
    mbxUserAtom
} from "../atoms/atoms";
import {
    createOrderAndBookingsRewrite,
    currencyFormat,
    saveNewPackage,
    sendOrderReceipt,
    sendProviderBookingConfirmation,
    updateHeldSlotsToConfirmedV2,
    updateOrderStatusToPaid,
    updateUsedQuantityOfPackage
} from "../modules/AdhocBookingService";
import {
    CLIENT_LOCATION_ID,
    PAY_TYPE_CC,
    PAY_TYPE_COLLECT_LATER,
    PAY_TYPE_NEW_CC,
    PAY_TYPE_NOT_REQUIRED,
    PAY_TYPE_PACKAGE
} from "../utils/Constants";
import { graphql, graphqlOperation, postApi } from "../modules/AmplifyServices";

import {
    createBillingTransaction,
    createClientCharge,
    updateClient
} from "../graphql/mutations";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import { MoneyAmountInput } from "../components/MoneyAmountInput";
import { getCurrencySymbolFromParam } from "../utils";
import LoadingButton from "@mui/lab/LoadingButton";
import { colors } from "../utils/Constants";
import { chargeAndProcessPayment } from "../utils/stripe";
import StripeChargeCard from "../components/stripe/StripeChargeCardAtomic";
import InfoIcon from "@mui/icons-material/Info";

export const PaymentForm = ({
    open,
    onClose,
    setMessage,
    onBookingCompleted
}) => {
    const theme = useTheme();
    const stripe = useStripe();
    const [editMode, setEditMode] = useState(false);
    /* const elements = useElements(); */
    const [elements, setElements] = useState(null);
    const { MB_GREY_LIGHT } = colors;
    const [, setProcessingPayment] = useState(false);
    const [processingCheckout, setProcessingCheckout] = useState(false);
    const [
        {
            client,
            cardExpired,
            isRepeating,
            selectedPackage,
            isPkgCredit,
            notes,
            uiSessionId,
            heldSlots,
            sessions,
            provider,
            price,
            payType,
            selectedPackageRemainingQuantity,
            providerLocation,
            slotType
        }
    ] = useAtom(bookingDetailsAtom);
    const [selectedLocation] = useAtom(getSelectedLocationAtom);
    const [timeDisplay] = useAtom(timeDisplayInfoAtom);
    const [bookingType] = useAtom(bookingTypeAtom);
    const [, setUserPageSnackMessage] = useAtom(userPageSnackbarAtom);
    const [company] = useAtom(companyAtom);
    const [currency] = useAtom(companyCurrencyAtom);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [{ orderSummary, charge }] = useAtom(orderSummaryAtom);
    const [selectedService] = useAtom(getSelectedServiceAtom);
    const [cbu, subtotal] = useAtom(chargeAtom);
    const [dueTodayAmount, setDueTodayAmount] = useState("0.00");
    const [isStripeChargeCardValid] = useAtom(isStripeChargeCardValidAtom);
    const [{ recurringWeekDays }] = useAtom(recurringSettingsAtom);
    const [upcomingBookingsDateTimeList] = useAtom(
        getUpcomingBookingsDateTimeListAtom
    );
    const { countrycode3166alpha2 } = selectedLocation;
    const [, setDetail] = useAtom(setDetailAtom);
    const [companyCollectsPayment] = useAtom(companyCollectsPaymentAtom);
    const [mbRecurringBookingRRule] = useAtom(mbRecurringBookingRRuleAtom);
    const isRecurringBooking = isRepeating && !selectedPackage;
    const zeroChargeBooking = charge?.total === 0 && !isRecurringBooking;
    const recurringZeroChargeBooking =
        isRecurringBooking &&
        selectedService.price === 0 &&
        dueTodayAmount === "0.00"; //determines if checkout is for a recurring booking that has no associated charge now or in the future

    const defaultOptionStyle = {
        minHeight: "40px",
        border: `1px solid ${MB_GREY_LIGHT}`,
        padding: "9px 18px",
        borderRadius: "4px"
    };

    const handleSetDueTodayAmount = (value) => {
        if (payType === PAY_TYPE_NOT_REQUIRED) {
            if (!companyCollectsPayment) {
                setDetail({ payType: PAY_TYPE_COLLECT_LATER });
            } else if (client?.defaultpartialcc) {
                setDetail({ payType: PAY_TYPE_CC });
            } else {
                setDetail({ payType: PAY_TYPE_NEW_CC });
            }
        }
        if (
            !isNaN(value) &&
            value !== undefined &&
            value !== null &&
            value !== ""
        ) {
            setDueTodayAmount(Number(value).toFixed(2));
        } else {
            setDueTodayAmount("0.00");
        }
    };

    const getRadioOptions = ({ processingCheckout }) => {
        const options = [];

        //Start with option 5 incase we need to exit early - as this option is always shown alone
        // Option 5: Recurring booking with zero charge
        if (recurringZeroChargeBooking) {
            options.push(
                <Grid
                    key={PAY_TYPE_NOT_REQUIRED}
                    container
                    alignItems={"center"}
                    sx={{
                        ...defaultOptionStyle
                    }}
                >
                    <FormControlLabel
                        value={PAY_TYPE_NOT_REQUIRED}
                        control={
                            <Radio
                                sx={{ color: MB_GREY_LIGHT }}
                                disabled={true}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant={"body1"}>
                                {`Payment not required`}
                            </Typography>
                        }
                    />
                </Grid>
            );
            return options;
        }

        // Option 1: Use package credits (only shown if package is paid)
        if (selectedPackage && selectedPackage?.status === "paid") {
            return (
                <Grid
                    key={PAY_TYPE_PACKAGE}
                    sx={{
                        ...defaultOptionStyle,
                        borderColor:
                            payType === PAY_TYPE_PACKAGE &&
                            !processingCheckout &&
                            theme.palette.primary.main,
                        borderWidth: payType === PAY_TYPE_PACKAGE && "2px"
                    }}
                >
                    <FormControlLabel
                        value={PAY_TYPE_PACKAGE}
                        control={
                            <Radio
                                sx={{ color: MB_GREY_LIGHT }}
                                disabled={processingCheckout}
                                size="small"
                            />
                        }
                        label={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column"
                                }}
                            >
                                <Typography>
                                    {`Use package credits (${selectedPackageRemainingQuantity ?? ""} remaining)`}
                                </Typography>
                            </div>
                        }
                    />
                </Grid>
            );
        }

        // Option 2: Pay with existing card (always shown if client has a default card)
        if (companyCollectsPayment && client?.defaultpartialcc) {
            options.push(
                <Grid
                    key={PAY_TYPE_CC}
                    container
                    alignItems={"center"}
                    sx={{
                        ...defaultOptionStyle,
                        borderColor:
                            payType === PAY_TYPE_CC &&
                            !processingCheckout &&
                            !zeroChargeBooking &&
                            theme.palette.primary.main,
                        borderWidth: payType === PAY_TYPE_CC && "2px"
                    }}
                >
                    <FormControlLabel
                        value={PAY_TYPE_CC}
                        control={
                            <Radio
                                sx={{ color: MB_GREY_LIGHT }}
                                disabled={
                                    cardExpired ||
                                    processingCheckout ||
                                    zeroChargeBooking
                                }
                                size="small"
                            />
                        }
                        label={
                            <Typography
                                variant={cardExpired ? "disabled" : "body1"}
                            >
                                {`Pay with existing card ending with ${client?.defaultpartialcc}`}
                            </Typography>
                        }
                    />
                </Grid>
            );
        }

        // Option 3: Pay with new card (shown if price is not zero)
        if (companyCollectsPayment) {
            options.push(
                <Grid
                    key="newCard"
                    container
                    alignItems={"center"}
                    sx={{
                        ...defaultOptionStyle,
                        borderColor:
                            payType === PAY_TYPE_NEW_CC &&
                            !processingCheckout &&
                            !zeroChargeBooking &&
                            theme.palette.primary.main,
                        borderWidth: payType === PAY_TYPE_NEW_CC && "2px",
                        padding:
                            payType === PAY_TYPE_NEW_CC ? "18px" : "9px 18px"
                    }}
                    xs={12}
                >
                    <StripeChargeCard
                        onProcessPayment={processCheckout}
                        onBookingCompleted={onClose}
                        setProcessingPayment={setProcessingPayment}
                        setElements={(elements) => setElements(elements)}
                        disabled={processingCheckout || zeroChargeBooking}
                    />
                </Grid>
            );
        }

        // Option 4: Payment will be collected later (always shown if company does not collect payment)
        if (!companyCollectsPayment) {
            options.push(
                <Grid
                    key={PAY_TYPE_COLLECT_LATER}
                    container
                    alignItems={"center"}
                    sx={{
                        ...defaultOptionStyle,
                        borderColor:
                            payType === PAY_TYPE_COLLECT_LATER &&
                            !processingCheckout &&
                            theme.palette.primary.main,
                        borderWidth: payType === PAY_TYPE_COLLECT_LATER && "2px"
                    }}
                >
                    <FormControlLabel
                        value={PAY_TYPE_COLLECT_LATER}
                        control={
                            <Radio
                                sx={{ color: MB_GREY_LIGHT }}
                                disabled={processingCheckout}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant={"body1"}>
                                {`Payment will be collected later`}
                            </Typography>
                        }
                    />
                </Grid>
            );
        }

        return options;
    };

    const processCheckout = async () => {
        if (processingCheckout) return;

        try {
            setProcessingCheckout(true);
            const isRemoteLocation = selectedLocation.id === CLIENT_LOCATION_ID;
            const bookingLocation = isRemoteLocation
                ? {
                      ...selectedLocation,
                      id: providerLocation.id,
                      latitude:
                          selectedLocation.latitude ??
                          providerLocation.latitude,
                      longitude:
                          selectedLocation.longitude ??
                          providerLocation.longitude
                  }
                : selectedLocation;

            //First, proceed with payment collection if company accepts payments, and payType includes PAY_TYPE_NEW_CC or PAY_TYPE_CC
            let stripeCharge;
            if (
                companyCollectsPayment &&
                (payType === PAY_TYPE_NEW_CC || payType === PAY_TYPE_CC) &&
                ((!isRecurringBooking && charge?.total > 0) ||
                    (isRecurringBooking && Number(dueTodayAmount) > 0))
            ) {
                stripeCharge = await processStripePayment();
                if (stripeCharge?.error) {
                    setProcessingCheckout(false);
                    throw new Error(stripeCharge.error, { cause: "stripe" });
                }
            }

            //Next, update package or create new package for the client if package booking
            let clientPackageId;
            if (isPkgCredit) {
                await updateUsedQuantityOfPackage({
                    pkg: selectedPackage,
                    increaseby: sessions?.length ?? 0
                });
                clientPackageId = selectedPackage?.id;
            } else if (selectedPackage) {
                const usedQuantity = heldSlots?.length || 0;
                const newClientPackage = await saveNewPackage({
                    user: client?.user || {},
                    companyId: company?.id,
                    clientId: client?.id,
                    serviceTypeId: selectedPackage?.servicetype?.id,
                    usedQuantity,
                    initialQuantity: selectedPackage?.quantity,
                    selectedPackageId: selectedPackage?.id
                });
                clientPackageId = newClientPackage?.data?.id;
            }

            //Next, create order and bookings
            const { order, bookingsList } = await createOrderAndBookingsRewrite(
                {
                    bookedByUser: mbxUser,
                    uiSessionId,
                    clientNotes: notes,
                    currency,
                    bookingType,
                    charge,
                    company,
                    service: selectedService,
                    location: bookingLocation,
                    heldSlots,
                    usedPkgIdForCredit: selectedPackage
                        ? clientPackageId
                        : null,
                    orderSummary: {
                        ...orderSummary,
                        ...(isRecurringBooking &&
                            Number(dueTodayAmount) > 0 && {
                                dueToday: dueTodayAmount
                            })
                    },
                    chargeBreakup: charge,
                    client,
                    isRepeating,
                    dateInfo: timeDisplay,
                    provider,
                    sessions,
                    slotType,
                    rrule: mbRecurringBookingRRule,
                    timeDisplay,
                    recurringWeekDays: recurringWeekDays,
                    isRemoteLocation,
                    upcomingBookingsDateTimeList,
                    mbxUserId: mbxUser?.id
                }
            );

            let input = {
                billingTransactionClientId: client.id,
                clientId: client.id,
                amount: Number.parseFloat(
                    Number.parseFloat(cbu.total ? cbu.total : 0).toFixed(2)
                ),
                billingTransactionCreatorId: client.user.username,
                creatorId: client.user.username,
                billingTransactionCompanyId: company.id,
                companyId: company.id,
                desc: `Payment for ${selectedService.name}`,
                billingTransactionOrderId: order.id,
                orderId: order.id
            };

            const billingTransaction = await graphql(
                graphqlOperation(createBillingTransaction, { input })
            );

            const promises = [];
            if (!isRepeating || selectedPackage) {
                promises.push(updateHeldSlotsToConfirmedV2(heldSlots));
            }
            promises.push(
                sendOrderReceipt(order.id),
                sendProviderBookingConfirmation({
                    provider: provider,
                    client: client,
                    company: company,
                    serviceType: selectedService,
                    bookings: bookingsList,
                    orderNo: order.orderNo,
                    orderType: order.type,
                    orderNotes: order.clientnotes
                }).catch((e) => {
                    console.log("error sending provider notifications ", e);
                })
            );
            await Promise.all(promises);

            //Create ClientCharge
            if (
                payType !== PAY_TYPE_PACKAGE &&
                charge?.total > 0 &&
                stripeCharge
            ) {
                const input = {
                    clientId: client.id,
                    clientChargeClientId: client.id,
                    companyId: company.id,
                    description: `Payment for ${selectedService.name}`,
                    status: "created",
                    stripechargeid: stripeCharge.charge.id,
                    stripepaymentmethod: stripeCharge.charge.payment_method,
                    stripe_payment_method_details:
                        stripeCharge.charge.stripe_payment_method_details,
                    balance_transaction:
                        stripeCharge.charge.balance_transaction,
                    stripe_status: stripeCharge.charge.status,
                    clientChargeBillingtransactionId:
                        billingTransaction.data.createBillingTransaction.id,
                    billingtransactionId:
                        billingTransaction.data.createBillingTransaction.id,
                    amount: Number.parseFloat(
                        Number.parseFloat(
                            stripeCharge.charge.amount / 100
                        ).toFixed(2)
                    ),
                    amount_refunded: 0,
                    currency: currency,
                    clientChargeOrderId: order.id,
                    orderId: order.id
                };
                await graphql(graphqlOperation(createClientCharge, { input }));
            }

            await updateOrderStatusToPaid(order.id, cbu);
            setUserPageSnackMessage({
                open: true,
                message: "Order processed successfully",
                severity: "success"
            });
            onBookingCompleted();
            setProcessingCheckout(false);
        } catch (error) {
            console.log("Error processing payment", error);
            setProcessingCheckout(false);
            if (error.cause === "stripe") {
                setUserPageSnackMessage({
                    open: true,
                    message: `Unable to process payment: ${error.message}`,
                    severity: "error"
                });
            } else {
                setUserPageSnackMessage({
                    open: true,
                    message: "Unable to process your payment",
                    severity: "error"
                });
            }
        }
    };

    const processStripePayment = async () => {
        try {
            // If no existing customer ID or using different card, create new token
            const toPay =
                isRecurringBooking && Number(dueTodayAmount) > 0
                    ? { total: Number(dueTodayAmount) }
                    : cbu;
            if (
                !client.stripeCustomerId ||
                payType === PAY_TYPE_NEW_CC ||
                cardExpired
            ) {
                const cardElement = elements.getElement(CardNumberElement);
                const connectedAccount =
                    company.stripeConnectEnabled && company.stripeAccount
                        ? { stripeAccount: company.stripeAccount }
                        : null;

                // Create token
                const { token, error: tokenError } = await stripe.createToken(
                    cardElement,
                    connectedAccount
                );
                if (tokenError) {
                    console.error(
                        "Error while creating stripe token:",
                        tokenError
                    );
                    return;
                }

                // Save card info
                const resultStripe = await postApi(
                    "stripesavecard",
                    "/savecardinfo",
                    {
                        body: {
                            token,
                            emailaddress: client.user.emailaddress,
                            companyId: company.id,
                            stripeAccount: company.stripeAccount,
                            stripeConnectEnabled: company.stripeConnectEnabled
                        }
                    }
                );

                if (resultStripe.error) {
                    console.error(
                        "Error while saving credit card information:",
                        resultStripe.error
                    );
                    return;
                }

                if (!resultStripe.customer) {
                    console.error("Could not save credit card information.");
                    return;
                }

                // Update client with new card info
                const stripeCustomerId = resultStripe.customer.id;
                const cclast4 = resultStripe.customer.sources.data[0].last4;
                const input = {
                    id: client.id,
                    defaultpartialcc: cclast4,
                    stripeCustomerId
                };

                const updatedClient = await graphql(
                    graphqlOperation(updateClient, { input })
                );
                const stripeCharge = await chargeAndProcessPayment({
                    client: updatedClient.data.updateClient,
                    company,
                    cbu: toPay,
                    currency
                });
                if (stripeCharge?.error) {
                    setProcessingCheckout(false);
                    throw new Error(stripeCharge.error);
                }
                return stripeCharge;
            } else {
                const stripeCharge = await chargeAndProcessPayment({
                    client,
                    company,
                    cbu: toPay,
                    currency
                });
                if (stripeCharge?.error) {
                    setProcessingCheckout(false);
                    throw new Error(stripeCharge.error);
                }
                return stripeCharge;
            }
        } catch (error) {
            console.error("Error while processing booking:", error);
            setUserPageSnackMessage({
                open: true,
                message: "Error processing payment",
                severity: "error"
            });
            return { error: error.message };
        }
    };

    return (
        <Dialog open={open} onClose={processingCheckout ? undefined : onClose}>
            <DialogTitle>Checkout</DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "18px",
                    paddingBottom: "2px"
                }}
            >
                {/* <OrderSummary /> */}
                <FormControl sx={{ display: "flex" }}>
                    <RadioGroup
                        value={payType ? payType : ""}
                        onChange={(e) => {
                            setDetail({ payType: e.target.value });
                        }}
                        sx={{ gap: "18px" }}
                    >
                        {getRadioOptions({ processingCheckout })}
                    </RadioGroup>
                </FormControl>
                <Grid container xs={12} gap={2.25}>
                    {isRecurringBooking && !recurringZeroChargeBooking && (
                        <Grid item xs={12}>
                            <Alert
                                sx={{
                                    display: "flex",
                                    padding: "10px 36px",
                                    border: `1px solid ${theme.palette.info.main}`,
                                    marginBottom: "0px",
                                    backgroundColor:
                                        theme.palette.info.lightest,
                                    textAlign: "left"
                                }}
                                severity="info"
                                icon={<InfoIcon />}
                            >
                                <Typography
                                    color={theme.palette.info.main}
                                    fontWeight={500}
                                    display={"inline"}
                                >
                                    WARNING: Payment for recurring bookings is
                                    processed when each session is completed{" "}
                                    {dueTodayAmount <= 0 &&
                                        "– no payment is due today"}
                                </Typography>
                            </Alert>
                        </Grid>
                    )}
                    {!(payType === PAY_TYPE_PACKAGE) && (
                        <Grid
                            item
                            xs={12}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                            <Grid item>
                                <Button
                                    variant="text"
                                    onClick={() => setEditMode(!editMode)}
                                    disableRipple
                                    sx={{
                                        "&:hover": {
                                            backgroundColor:
                                                "transparent !important"
                                        }
                                    }}
                                    disabled={processingCheckout}
                                >
                                    <Typography
                                        variant="button"
                                        sx={{
                                            textTransform: "none",
                                            lineHeight: "16px"
                                        }}
                                    >
                                        {`Edit amounts`}
                                    </Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container xs={12} gap={editMode ? 1.125 : 0}>
                        <Grid
                            item
                            xs={12}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                            <Grid item xs={3}>
                                <Typography variant="body1">
                                    Subtotal
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                {!(payType === PAY_TYPE_PACKAGE) && editMode ? (
                                    <MoneyAmountInput
                                        setAmount={(value) => {
                                            setDetail({
                                                overrideServicePrice: value
                                            });
                                        }}
                                        prefix={getCurrencySymbolFromParam(
                                            currency
                                        )}
                                        amount={orderSummary?.rows[0].rawPrice}
                                        disabled={
                                            isRecurringBooking ||
                                            processingCheckout
                                        }
                                        name="servicePrice"
                                    />
                                ) : (
                                    <Typography
                                        variant="body1"
                                        textAlign={"right"}
                                    >
                                        {orderSummary?.rows[0].price}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        {countrycode3166alpha2?.toUpperCase() === "CA" ? (
                            <>
                                {company.addServiceFee && (
                                    <ServiceFeeRow
                                        processingCheckout={processingCheckout}
                                        editMode={editMode}
                                        isRecurringBooking={isRecurringBooking}
                                    />
                                )}
                                <TaxRow
                                    taxAmount={orderSummary?.taxAmount}
                                    taxRate={orderSummary?.taxRate}
                                />
                            </>
                        ) : (
                            <>
                                <TaxRow
                                    taxAmount={orderSummary?.taxAmount}
                                    taxRate={orderSummary?.taxRate}
                                />
                                {company.addServiceFee && (
                                    <ServiceFeeRow
                                        processingCheckout={processingCheckout}
                                        editMode={editMode}
                                        isRecurringBooking={isRecurringBooking}
                                    />
                                )}
                            </>
                        )}

                        {isRecurringBooking && (
                            <Grid
                                container
                                item
                                xs={12}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end"
                                }}
                            >
                                <Grid item xs={3}>
                                    <Typography variant="body1">
                                        Due today
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    {!(payType === PAY_TYPE_PACKAGE) &&
                                    editMode ? (
                                        <MoneyAmountInput
                                            setAmount={(value) => {
                                                handleSetDueTodayAmount(value);
                                            }}
                                            prefix={getCurrencySymbolFromParam(
                                                currency
                                            )}
                                            amount={dueTodayAmount}
                                            disabled={processingCheckout}
                                            name="dueTodayAmount"
                                        />
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            textAlign={"right"}
                                        >
                                            {currencyFormat(
                                                dueTodayAmount,
                                                currency
                                            )}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </Grid>

                    <Grid
                        container
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                        <Grid
                            container
                            item
                            xs={12}
                            justifyContent={"flex-end"}
                        >
                            <Grid item xs={3}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: "black"
                                    }}
                                >
                                    TOTAL{isRecurringBooking ? "*" : ""}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: "black"
                                    }}
                                    textAlign={"right"}
                                >
                                    {orderSummary?.total}
                                </Typography>
                            </Grid>
                        </Grid>
                        {isRecurringBooking && (
                            <Grid
                                container
                                item
                                xs={12}
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end"
                                }}
                            >
                                <Grid item xs={3}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 700,
                                            color: "black"
                                        }}
                                    >
                                        DUE TODAY
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography
                                        variant="body1"
                                        textAlign={"right"}
                                        sx={{
                                            fontWeight: 700,
                                            color: "black"
                                        }}
                                    >
                                        {currencyFormat(
                                            dueTodayAmount,
                                            currency
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                    {isRecurringBooking && (
                        <Grid
                            container
                            xs={12}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                            <Grid
                                container
                                item
                                xs={12}
                                justifyContent={"flex-end"}
                            >
                                <Grid item xs={6}>
                                    <Typography variant="helperText">
                                        * = for each completed session
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ gap: "10px", paddingTop: "28px" }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={processingCheckout}
                >
                    Cancel
                </Button>
                <LoadingButton
                    color="primary"
                    variant="contained"
                    loading={processingCheckout}
                    onClick={processCheckout}
                    disabled={
                        payType === PAY_TYPE_NEW_CC &&
                        !isStripeChargeCardValid &&
                        !zeroChargeBooking
                    }
                >
                    {payType === PAY_TYPE_PACKAGE
                        ? `Use ${sessions?.length} credit${
                              sessions?.length > 1 ? "s" : ""
                          }`
                        : isRecurringBooking
                          ? dueTodayAmount === "0.00"
                              ? "CONFIRM BOOKING"
                              : payType === PAY_TYPE_COLLECT_LATER
                                ? "CONFIRM BOOKING"
                                : `Pay ${
                                      dueTodayAmount
                                          ? currencyFormat(
                                                dueTodayAmount,
                                                currency
                                            )
                                          : currencyFormat(0, currency)
                                  }`
                          : payType === PAY_TYPE_COLLECT_LATER
                            ? "CONFIRM BOOKING"
                            : charge?.total === 0
                              ? "CONFIRM BOOKING"
                              : `Pay ${orderSummary?.total}`}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

const TaxRow = ({ taxRate, taxAmount }) => (
    <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Grid item xs={3}>
            <Typography variant="body1">Tax ({taxRate}%)</Typography>
        </Grid>
        <Grid item xs={3}>
            <Typography variant="body1" textAlign={"right"}>
                {taxAmount}
            </Typography>
        </Grid>
    </Grid>
);
const ServiceFeeRow = ({
    processingCheckout,
    editMode,
    isRecurringBooking
}) => {
    const [{ payType }] = useAtom(bookingDetailsAtom);
    const [currency] = useAtom(companyCurrencyAtom);
    const [
        {
            orderSummary: { rawServiceFee, serviceFee }
        }
    ] = useAtom(orderSummaryAtom);
    const [, setDetail] = useAtom(setDetailAtom);
    return (
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Grid item xs={3}>
                <Typography variant="body1">Service fee</Typography>
            </Grid>
            <Grid item xs={3}>
                {!(payType === PAY_TYPE_PACKAGE) && editMode ? (
                    <MoneyAmountInput
                        setAmount={(value) => {
                            setDetail({ overrideServiceFee: value });
                        }}
                        prefix={getCurrencySymbolFromParam(currency)}
                        amount={rawServiceFee}
                        name="serviceFee"
                        disabled={processingCheckout || isRecurringBooking}
                    />
                ) : (
                    <Typography variant="body1" textAlign={"right"}>
                        {serviceFee}
                    </Typography>
                )}
            </Grid>
        </Grid>
    );
};
